'use client';

import React, { useMemo } from 'react';
import type { PageWrapperData } from '@/app/common/createPageWrapperData.ts';
import type { PAGE_TYPE } from '@/app/common/pageType.ts';
import { resolvePagePriceDisplayType } from '@/app/components/search/utils/formatting.ts';

export const PageWrapperContext = React.createContext<PageWrapperData>({} as PageWrapperData);

export default function PageWrapperProvider({
  pageWrapperData,
  pageType,
  children,
}: {
  pageWrapperData: PageWrapperData;
  pageType: PAGE_TYPE;
  children: React.ReactNode;
}) {
  // The price display type is the one page-dependent value in the config — the EVO detail steps round
  // where the results steps floor (MPH-872) — and the config is reused across in-app navigations
  // without a refetch, so the step's value is resolved here, from the page type of the step being
  // published, rather than frozen at the page the search landed on. Doing it in the provider rather
  // than in PageWrapperClient means every mount of the context has to name its step, so a route that
  // publishes the config without going through the funnel cannot silently serve the results value on
  // a detail page. (MPH-1101)
  const contextValue = useMemo(
    () => ({
      ...pageWrapperData,
      localization: {
        ...pageWrapperData.localization,
        configValuesApp: {
          ...pageWrapperData.localization.configValuesApp,
          priceDisplayType: resolvePagePriceDisplayType(pageType, pageWrapperData.localization.configValuesApp),
        },
      },
    }),
    [pageWrapperData, pageType]
  );
  return <PageWrapperContext.Provider value={contextValue}>{children}</PageWrapperContext.Provider>;
}

export const usePageWrapperData = () => React.useContext(PageWrapperContext);
